import React from 'react';
import { useRouter } from 'next/router';
import {
  getServerRuntimeConfig,
  getRuntimeConfig,
} from '../../lib/runtimeConfig';
import Head from 'next/head';
import { Grid, Box, Container } from '@mui/material';
import { type GetServerSidePropsContext } from 'next';
import { useI18nContext } from '@procore/core-react';
import sanitizeHtml from 'sanitize-html';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import { type Project, type ProjectTile } from '../../types/project';
import { canonicalUrl } from '../../helpers/canonical-url';
import { type GeoInfo, getGeoInfoFromProject } from '../../services/geo-info';
import { useStyles } from './[id].styles';
import { usePageView } from '../../components/Analytics/utilities';
import PublicProjectLayout from '../../components/Project/PublicProjectLayout';
import { absoluteUrl } from '../../helpers/absolute-url';
import { formatDatetime } from '../../helpers/format-datetime';
import { getBidDueStatus } from '../../helpers/build-texts';
import { stripNdaProjectData } from '../../helpers/strip-nda-project-data';
import { getNdaProjectDisplay } from '../../helpers/nda-display';
import { getLocationBreadcrumbs } from '../../helpers/location-breadcrumbs';
import { type BreadcrumbItem } from '../../components/Breadcrumbs/BreadcrumbContainer';
import GoogleBreadcrumbScript from '../../components/Google/GoogleBreadcrumbScript';
import { getEnrichedProject } from '../../services/project-enrichment';
import { type ProjectsSearchRequest } from '../../data/projectsSearch';
import { NEARBY_PROJECTS_RADIUS } from '../../constants';
import { getSearchResults } from '../../services/project-search';
import { paginationDefaults } from '../../types/pagination';
import DistanceUnit from '../../data/location/distance-units';
import { convertEsPublicBidPackageToProjectTile } from '../../data/monolith/project';
import Custom404 from '../404.page';

const publicRuntimeConfig = getRuntimeConfig();
const serverRuntimeConfig = getServerRuntimeConfig();

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { req, res } = context;
  const { origin } = absoluteUrl(req);

  const { id } = context.query;
  const workingId = id.toString();

  let project: Project | 'gone' | null | 'not in US' = await getEnrichedProject(
    workingId
  );

  /* istanbul ignore next */
  if (!project || project === 'not in US') {
    return {
      notFound: true,
    };
  }
  /* istanbul ignore next */
  if (project === 'gone') {
    res.statusCode = 410;
    return {
      props: {
        gone: true,
      },
    };
  }
  /* istanbul ignore next */
  if (project.bidPackagePrimarySlug !== workingId) {
    return {
      redirect: {
        destination: `${publicRuntimeConfig.basePath}/j/${project.bidPackagePrimarySlug}`,
        permanent: true,
      },
    };
  }

  const ndaDisplay = getNdaProjectDisplay(project);
  if (ndaDisplay.isNda) {
    project = stripNdaProjectData(project, ndaDisplay.showProjectName);
  }

  const sanitizeWithLinkUpdates = (text) => {
    return sanitizeHtml(text, {
      transformTags: {
        a: sanitizeHtml.simpleTransform('a', { target: '_blank' }),
      },
    });
  };

  /* istanbul ignore else */
  if (project.prebidMeeting.notes) {
    project.prebidMeeting.notes = sanitizeWithLinkUpdates(
      project.prebidMeeting.notes
    );
  }
  /* istanbul ignore else */
  if (project.siteWalkthrough.notes) {
    project.siteWalkthrough.notes = sanitizeWithLinkUpdates(
      project.siteWalkthrough.notes
    );
  }
  /* istanbul ignore else */
  if (project.biddingInstructions) {
    project.biddingInstructions = sanitizeWithLinkUpdates(
      project.biddingInstructions
    );
  }

  if (!project.location?.latitude) {
    project.location.latitude = 38.7946;
  }
  if (!project.location?.longitude) {
    project.location.longitude = 106.5348;
  }
  const geoInfo = getGeoInfoFromProject(project);

  // Enable the below once we have enough data on staging
  const nearbyRequest: ProjectsSearchRequest = {
    businessRegistryUrl: serverRuntimeConfig.businessRegistry,
    geoInfo,
    sectors: null,
    trades: null,
    classifications: null,
    query: null,
    page: paginationDefaults.page,
    pageSize: paginationDefaults.pageSize,
    radius: NEARBY_PROJECTS_RADIUS,
    unit: DistanceUnit.Mile,
    sort: 'relevance',
    loggingContext: `ProjectPage - getServerSideProps nearbyProject: ${project.bidPackagePrimarySlug}, ${project.bidPackageId}`,
  };
  const nearbyProjects: ProjectTile[] = (
    (await getSearchResults(nearbyRequest))?.results || []
  )
    .map((p) => convertEsPublicBidPackageToProjectTile(p))
    .filter((p) => p.id !== Number(project.bidPackageId));

  const sameCompanyRequest: ProjectsSearchRequest = {
    businessRegistryUrl: serverRuntimeConfig.businessRegistry,
    geoInfo: null,
    sectors: null,
    trades: null,
    classifications: null,
    query: null,
    page: paginationDefaults.page,
    pageSize: paginationDefaults.pageSize,
    radius: NEARBY_PROJECTS_RADIUS,
    unit: DistanceUnit.Mile,
    sort: 'due_soon',
    loggingContext: `ProjectPage - getServerSideProps sameCompanyProject: ${project.bidPackagePrimarySlug}, ${project.bidPackageId}`,
    companyId: project.companyId,
  };
  const sameCompanyProjects: ProjectTile[] = (
    (await getSearchResults(sameCompanyRequest))?.results || []
  )
    .map((p) => convertEsPublicBidPackageToProjectTile(p))
    .filter((p) => p.id !== Number(project.bidPackageId));

  return {
    props: {
      geoInfo,
      project,
      nearbyProjects,
      sameCompanyProjects,
      origin,
    },
  };
}

export default function PublicProject({
  geoInfo,
  project,
  nearbyProjects,
  sameCompanyProjects,
  origin,
  gone,
}: Readonly<{
  geoInfo: GeoInfo;
  project: Project;
  nearbyProjects: ProjectTile[];
  sameCompanyProjects: ProjectTile[];
  origin: string;
  gone?: boolean;
}>) {
  const router = useRouter();
  const servicesNeededMetaString = project?.tradesAndServices
    ? project.tradesAndServices
        .map((s) => {
          return s.key;
        })
        .join(', ')
    : '';
  const analyticsProps = {
    Name: project?.name,
    Id: project?.bidPackageId,
    City: project?.location?.city,
    State: project?.location?.stateCode,
    Services: servicesNeededMetaString,
  };
  usePageView('Public Project', analyticsProps);

  const classes = useStyles();

  const I18n = useI18nContext();
  const canonicalURL = canonicalUrl(router.asPath);
  const breadcrumbs: BreadcrumbItem[] = getLocationBreadcrumbs(
    geoInfo,
    null,
    null,
    true
  );

  if (gone) {
    // Mark-up is rendered as 404, but status will be 410 per res.statusCode = 410;
    return <Custom404 />;
  }

  let metaContent;
  const ndaFlags = getNdaProjectDisplay(project);
  const { status: bidStatus } = getBidDueStatus(project);
  if (ndaFlags.isNda) {
    metaContent =
      'This project requires a NDA. Request access to view project details and to receive an invitation to bid.';
  } else {
    const bidDueText =
      bidStatus === 'closed'
        ? 'Bidding Closed'
        : `Bids due on ${formatDatetime(project?.bidDueDate)}`;
    metaContent = `${bidDueText}, ${project?.projectSectorName} • ${project?.fundingType}. View project details and contact the solicitor on the Procore Construction Network`;
  }
  let titleLocation;
  if (project.location?.city && project.location?.stateCode) {
    titleLocation = ` - ${project.location?.city}, ${project.location?.stateCode}`;
  } else if (!project.location?.city && project.location?.stateCode) {
    titleLocation = ` - ${project.location?.stateCode}`;
  } else if (project.location?.city && !project.location?.stateCode) {
    titleLocation = ` - ${project.location?.city}`;
  } else {
    titleLocation = '';
  }

  return (
    <>
      <Head>
        <title>
          {I18n.t('pages.j.id.procore', {
            name: project.name
              ? project.name
              : titleLocation
              ? 'Project In '
              : '',
            location: titleLocation,
          })}
        </title>
        <meta name="description" content={metaContent} />
        {ndaFlags.isNda && <meta name="robots" content="noindex" />}
        <link rel="canonical" key="canonical" href={canonicalURL} />
      </Head>
      <GoogleBreadcrumbScript breadcrumbs={breadcrumbs} origin={origin} />
      <Grid container>
        <Header />
        <Grid
          justifyContent="center"
          container
          item
          component="section"
          className={classes.businessProfileLayout}
          role="main"
        >
          <Grid
            className={classes.innerGridContainer}
            component="section"
            container
            spacing={3}
          >
            <Container className={classes.container}>
              <Box mt={4} mb={10} style={{ marginBottom: '30px' }}>
                <PublicProjectLayout
                  project={project}
                  nearbyProjects={nearbyProjects}
                  sameCompanyProjects={sameCompanyProjects}
                  breadcrumbs={breadcrumbs}
                />
              </Box>
            </Container>
          </Grid>
        </Grid>
        <Footer />
      </Grid>
    </>
  );
}
